import { db, auth } from "@/lib/firebase";
import { 
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  DocumentData
} from "firebase/firestore";

/**
 * Service for handling user reports data and operations
 * This service ensures compatibility with the hybrid Firebase approach
 * by using the modular API exclusively
 */
export const userReportsService = {
  /**
   * Get the company ID for the current user
   */
  async getCurrentUserCompanyId(): Promise<string | null> {
    try {
      if (!auth.currentUser) return null;
      
      const userRef = doc(db, "users", auth.currentUser.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        throw new Error("User data not found");
      }
      
      return userSnap.data().companyId;
    } catch (error) {
      console.error("Error getting company ID:", error);
      return null;
    }
  },

  /**
   * Get company information by ID
   */
  async getCompanyInfo(companyId: string): Promise<DocumentData | null> {
    try {
      const companyRef = doc(db, "customers", companyId);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        throw new Error("Company data not found");
      }
      
      return companySnap.data();
    } catch (error) {
      console.error("Error getting company info:", error);
      return null;
    }
  },

  /**
   * Get all users for a company
   */
  async getCompanyUsers(companyId: string): Promise<any[]> {
    try {
      const companyRef = doc(db, "customers", companyId);
      const companySnap = await getDoc(companyRef);
      
      if (!companySnap.exists()) {
        throw new Error("Company document does not exist");
      }
      
      // Get unique users
      const customerOrgUsers = companySnap.data().customerOrg.users;
      return Object.values(customerOrgUsers);
    } catch (error) {
      console.error("Error getting company users:", error);
      return [];
    }
  },

  /**
   * Get user information by ID
   */
  async getUserInfo(userId: string): Promise<DocumentData | null> {
    try {
      const userRef = doc(db, "users", userId);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        return null;
      }
      
      return userSnap.data();
    } catch (error) {
      console.error("Error getting user info:", error);
      return null;
    }
  },

  /**
   * Get assigned policies for a user
   */
  async getUserPolicies(userId: string): Promise<any[]> {
    try {
      const policiesRef = collection(db, "users", userId, "assignedPolicies");
      const policiesSnap = await getDocs(policiesRef);
      
      return policiesSnap.docs;
    } catch (error) {
      console.error("Error getting user policies:", error);
      return [];
    }
  },

  /**
   * Get quiz questions for a policy
   */
  async getQuizQuestions(policyRef: string, companyId: string): Promise<any[]> {
    try {
      const path = `customers/${companyId}/product/policyPackages/policies/${policyRef}/screens`;
      const screensRef = collection(db, path);
      const screensSnap = await getDocs(screensRef);
      
      // Filter and map quiz questions
      return screensSnap.docs
        .filter(doc => doc.id.includes("quiz"))
        .map(doc => doc.data());
    } catch (error) {
      console.error("Error getting quiz questions:", error);
      return [];
    }
  },

  /**
   * Get quiz attempts for a user's policy
   */
  async getQuizAttempts(userId: string, policyRef: string): Promise<any[]> {
    try {
      const attemptsPath = `users/${userId}/assignedPolicies/${policyRef}/attempts`;
      const attemptsRef = collection(db, attemptsPath);
      const attemptsSnap = await getDocs(attemptsRef);
      
      if (attemptsSnap.empty) {
        return [];
      }
      
      // Extract attempts data
      return attemptsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting quiz attempts:", error);
      return [];
    }
  },

  /**
   * Process quiz results from attempts
   */
  processQuizResults(attempts: any[]): {
    fullCorrectAttempts: number;
    percentage: string;
    mostRecent: any | null;
  } {
    if (!attempts || attempts.length === 0) {
      return { 
        fullCorrectAttempts: 0, 
        percentage: "0%",
        mostRecent: null
      };
    }
    
    // Find most recent attempt
    const mostRecent = attempts.reduce((latest, current) => {
      const latestTime = latest.timestamp?.toDate?.() || new Date(latest.timestamp);
      const currentTime = current.timestamp?.toDate?.() || new Date(current.timestamp);
      
      return currentTime > latestTime ? current : latest;
    });
    
    // Count fully correct attempts
    const fullCorrectAttempts = attempts.filter(attempt => {
      return this.checkAnswers(attempt).allCorrect;
    }).length;
    
    // Calculate percentage
    const percentage = Math.round((fullCorrectAttempts / attempts.length) * 100);
    
    return {
      fullCorrectAttempts,
      percentage: `${percentage}%`,
      mostRecent
    };
  },

  /**
   * DED-1239: Check if a policy has a quiz available
   * PDF policies don't have quizzes, so they shouldn't count toward quiz completion
   */
  async policyHasQuiz(policyId: string, companyId: string): Promise<boolean> {
    try {
      const policyRef = doc(db, `customers/${companyId}/product/policyPackages/policies/${policyId}`);
      const policySnap = await getDoc(policyRef);
      
      if (!policySnap.exists()) {
        return false;
      }
      
      const policyData = policySnap.data();
      // Check if quiz_location exists and is a valid DocumentReference
      const quizLocation = policyData?.quiz_location;
      return !!(quizLocation && typeof quizLocation === 'object' && quizLocation.path && quizLocation.path.trim().length > 0);
    } catch (error) {
      console.error("Error checking if policy has quiz:", error);
      return false;
    }
  },

  /**
   * Get quiz results for a specific policy and user
   */
  async getPolicyQuizResults(
    userId: string, 
    policyId: string,
    companyId: string
  ): Promise<Record<string, any> | null> {
    try {
      const quizResultsRef = doc(db, 
        `users/${userId}/assignedPolicies/${policyId}/policy_quiz_results/results`
      );
      
      const quizResultsSnap = await getDoc(quizResultsRef);
      
      if (!quizResultsSnap.exists()) {
        return null;
      }
      
      return quizResultsSnap.data();
    } catch (error) {
      console.error("Error getting policy quiz results:", error);
      return null;
    }
  },

  /**
   * Check if answers in a quiz attempt are correct
   */
  checkAnswers(data: any): {
    correctCount: number;
    totalQuestions: number;
    allCorrect: boolean;
  } {
    let correctCount = 0;
    let totalQuestions = 0;
    
    // Check each question and answer
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        const match = key.match(/^(\d+)GivenAnswer$/);
        if (match) {
          const questionNumber = match[1];
          const correctAnswer = data[`${questionNumber}CorrectAnswer`];
          const givenAnswer = data[key];
          
          if (correctAnswer !== undefined && givenAnswer !== undefined) {
            totalQuestions++;
            if (correctAnswer === givenAnswer) {
              correctCount++;
            }
          }
        }
      }
    }
    
    // Determine if all answers are correct
    const allCorrect = totalQuestions > 0 && correctCount === totalQuestions;
    
    return {
      correctCount,
      totalQuestions,
      allCorrect
    };
  }
};
